"use client";
import { useCallback } from "react";
import { useGigya } from "@dxp-feed/gigya";

const CACHE_KEY = "gigya_uid_cache";
const CACHE_DURATION_MS = 55 * 1000; // 55 seconds

let isFetching: Promise<{
  idToken: string;
  uid: string;
  cardNumber: string;
  name: string;
}> | null = null;

export const useGigyaAccountInfo = () => {
  const { gigya } = useGigya();

  const getIdToken = useCallback((): Promise<{
    idToken: string;
    uid: string;
    cardNumber: string;
    name: string;
  }> => {
    if (!gigya?.accounts?.getJWT) {
      console.warn("Gigya not initialized");
      return Promise.resolve({
        idToken: "",
        uid: "",
        cardNumber: "",
        name: "",
      });
    }

    const now = Date.now();
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      const parsed = cached ? JSON.parse(cached) : null;

      if (parsed?.idToken && now - parsed.cachedAt < CACHE_DURATION_MS) {
        return Promise.resolve({
          idToken: parsed.idToken,
          uid: parsed.uid,
          cardNumber: parsed.cardNumber,
          name: parsed.name,
        });
      }
    } catch (e) {
      console.warn("Gigya UID cache parse failed:", e);
    }

    if (isFetching) return isFetching;

    isFetching = new Promise((resolve) => {
      gigya.accounts.getJWT({
        fields: "data.scene.cardNumber,profile.firstName",
        callback: (response: any) => {
          const idToken = response?.errorCode === 0 ? response?.id_token : "";
          const uid = response?.errorCode === 0 ? response?.uid : "";
          const cardNumber =
            response?.errorCode === 0 ? response?.cardNumber : "";
          const name = response?.errorCode === 0 ? response?.name : "";

          const newCache = {
            idToken,
            cachedAt: Date.now(),
            uid,
            cardNumber,
            name,
          };

          try {
            sessionStorage.setItem(CACHE_KEY, JSON.stringify(newCache));
          } catch (e) {
            console.warn("Gigya UID cache set failed:", e);
          }

          resolve({ idToken, uid, cardNumber, name });
          isFetching = null;
        },
      });
    });

    return isFetching;
  }, [gigya]);

  return getIdToken;
};

export const flushToken = () => {
  return sessionStorage.removeItem(CACHE_KEY);
};
